import { useState, useEffect } from "react";
import AdminUnlockDialog from "./components/AdminUnlockDialog";
import OperatorLogin from "./components/OperatorLogin";
import Checklist from "./components/Checklist";
import DagAfsluiting from "./components/DagAfsluiting";
import Dagstart, { DagstartPlan } from "./components/Dagstart";
import UpdateChecker from "./components/UpdateChecker";
import Dashboard from "./pages/Dashboard";
import LogsView from "./pages/LogsView";
import DeploymentView from "./pages/DeploymentView";
import AdminAccessView from "./pages/AdminAccessView";
import SoftwareView from "./pages/SoftwareView";
import SettingsView from "./pages/SettingsView";
import TestLabView from "./pages/TestLabView";
import InsightsView from "./pages/InsightsView";
import FileDropView from "./pages/FileDropView";
import QueueDisplay from "./pages/QueueDisplay";
import { SettingsProvider } from "./contexts/SettingsContext";
import SupabaseEnvBadge from "./components/SupabaseEnvBadge";
import "./styles/logs-view.css";
import "./styles/deployment-view.css";
import { createPCAgentClient } from "./services/pcAgent";
import { databaseService } from "./services/database";
import { wakeMachines } from "./services/wake";
import { storage } from "./utils/storage";
import { Operator, Room } from "./types/database";
import { isAdmin, Permission } from "./utils/permissions";
import { preloadAllRolePermissions, getRolePermissions } from "./services/rolePermissions";
import { operatorAuthService } from "./services/operatorAuth";
import { reconcileOperatorDevice } from "./services/operatorDevice";
import "./styles/index.css";
import "./styles/mobile.css";
import "./styles/queue.css";

// The Electron secondary BrowserWindow loads this app at hash route
// #/queue-display and short-circuits straight into the customer-facing
// queue view. The public web build (VITE_DISPLAY_ONLY=true) also lands
// every URL directly on the display — that build is shipped as a
// standalone "neoseum-queue" Cloudflare Pages project so a TV can hit
// https://queue.neoseum.com/?locationId=... without the hash prefix.
// The main Electron operator window keeps the full auth flow.
function isQueueDisplayRoute(): boolean {
  if (typeof window === 'undefined') return false;
  if (import.meta.env.VITE_DISPLAY_ONLY === 'true') return true;
  return window.location.hash.startsWith('#/queue-display');
}

interface AppConfig {
  defaultRoomId?: string;
}

// After flagging PCs for Wake-on-LAN, wait this long before restarting the PCs
// that are still up. The agent's waker re-polls the wake flag every 15s and
// sends packets every 5s (shared/network/network.go runWaker), so without this
// pause a Dagstart in a half-on room can take every sender down before a single
// magic packet reaches the wire.
const DAGSTART_WAKE_GRACE_MS = 20000;

export default function App() {
  // Customer-facing second screen: render the public queue view and skip
  // the entire auth state machine. Electron's session sharing means this
  // window's Supabase client picks up the same anon key as the main window.
  if (isQueueDisplayRoute()) {
    return (
      <>
        <SupabaseEnvBadge />
        <QueueDisplay />
      </>
    );
  }

  // Config is no longer needed since Dashboard manages its own room selection
  // const [config] = useState<AppConfig>(() => {
  //   const savedRoomId = localStorage.getItem('selectedRoomId');
  //   return {
  //     defaultRoomId: savedRoomId || import.meta.env.VITE_DEFAULT_ROOM_ID || undefined,
  //   };
  // });
  const [showAdminDialog, setShowAdminDialog] = useState(false);
  const [showDagAfsluiting, setShowDagAfsluiting] = useState(false);
  const [dagstartPlan, setDagstartPlan] = useState<DagstartPlan | null>(null);
  const [dagstartChecking, setDagstartChecking] = useState(false);
  const [dagstartBusy, setDagstartBusy] = useState(false);
  const [dagstartProgress, setDagstartProgress] = useState<string | null>(null);
  // Reported by the dashboard tiles; drives the disabled state of the Dagstart
  // button. Only authoritative while the dashboard is mounted, so the click
  // handler re-checks every reachable agent before it offers to confirm.
  const [runningExperiencePcIds, setRunningExperiencePcIds] = useState<string[]>([]);
  const [kioskMode, setKioskMode] = useState(true);
  const [operatorLoggedIn, setOperatorLoggedIn] = useState(false);
  const [currentOperator, setCurrentOperator] = useState<Operator | null>(null);
  const [checklistCompleted, setChecklistCompleted] = useState(false);
  const [pcInstances, setPcInstances] = useState<any[]>([]);
  const [operatorPermissions, setOperatorPermissions] = useState<string[]>([]);

  // Room selection state (lifted from Dashboard)
  const [rooms, setRooms] = useState<Room[]>([]);
  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [activeView, setActiveView] = useState<'dashboard' | 'logs' | 'deployments' | 'admin' | 'software' | 'settings' | 'testlab' | 'insights' | 'filedrop'>('dashboard');

  // Preload role permissions on app startup
  useEffect(() => {
    preloadAllRolePermissions();
  }, []);

  // Register this device + reconcile it toward its pinned Operator version.
  // Runs on launch and whenever the reported room changes; on Android an OTA
  // web-bundle swap (if a version is pinned) is applied silently on next
  // launch/foreground (never mid-session). No-op self-report on web/electron.
  useEffect(() => {
    reconcileOperatorDevice(selectedRoomId);
  }, [selectedRoomId]);

  // Re-reconcile when the app returns to the foreground, so a version pinned
  // while the tablet was idle is picked up without needing a manual relaunch.
  useEffect(() => {
    if (!window.Capacitor?.isNativePlatform?.()) return;
    let remove: (() => void) | undefined;
    import("@capacitor/app").then(({ App }) => {
      App.addListener("resume", () => {
        reconcileOperatorDevice(selectedRoomId);
      }).then((handle) => {
        remove = () => handle.remove();
      });
    });
    return () => remove?.();
  }, [selectedRoomId]);

  // Load rooms on mount
  useEffect(() => {
    const loadRooms = async () => {
      try {
        const roomsList = await databaseService.listRooms();
        setRooms(roomsList);

        const savedRoomId = await storage.getItem('selectedRoomId');
        if (savedRoomId && roomsList.some(room => room.id === savedRoomId)) {
          setSelectedRoomId(savedRoomId);
        } else if (roomsList.length > 0) {
          setSelectedRoomId(roomsList[0].id);
        }
      } catch (err) {
        console.error('Failed to load rooms:', err);
      }
    };
    loadRooms();
  }, []);

  const handleRoomChange = async (roomId: string) => {
    setSelectedRoomId(roomId);
    if (roomId) {
      await storage.setItem('selectedRoomId', roomId);
    }
  };

  // Setup Electron listeners
  useEffect(() => {
    if (!window.electron) return;

    // Listen for admin unlock dialog request
    window.electron.onShowAdminUnlock(() => {
      setShowAdminDialog(true);
    });

    // Listen for kiosk status updates
    window.electron.onKioskStatus((status: { kioskMode: boolean }) => {
      setKioskMode(status.kioskMode);
    });

    // Get initial kiosk status
    window.electron.getKioskStatus();
  }, []);

  // Lock kiosk mode
  const handleLockKiosk = () => {
    if (window.electron) {
      window.electron.lockKiosk();
      setKioskMode(true);
    }
  };

  const handleOperatorLogin = async (operator: Operator) => {
    setCurrentOperator(operator);
    setOperatorLoggedIn(true);
    console.log('Operator logged in:', operator);

    // Load operator permissions (role + per-operator JSONB)
    const rolePerms = await getRolePermissions(operator.role);
    const operatorPerms = operator.permissions
      ? Object.keys(operator.permissions).filter(k => operator.permissions![k] === true)
      : [];
    const merged = Array.from(new Set([...rolePerms, ...operatorPerms]));
    setOperatorPermissions(merged);
    console.log('Operator permissions:', merged);
  };

  const handleOperatorLogout = () => {
    // Clear operator session
    operatorAuthService.clearOperatorSession();

    // Reset state
    setCurrentOperator(null);
    setOperatorLoggedIn(false);
    setChecklistCompleted(false);
    setOperatorPermissions([]);

    console.log('Operator logged out');
  };

  // Check if current operator is admin
  const operatorIsAdmin = isAdmin(currentOperator);

  // Check if operator has dag_afsluiting permission
  const canCloseDag = operatorPermissions.includes(Permission.DAG_AFSLUITING);

  // Check if operator can see and launch beta/alpha releases
  const canViewBetaReleases = operatorIsAdmin || operatorPermissions.includes(Permission.ACCESS_BETA_RELEASES);

  const handleDagAfsluitingComplete = async () => {
    // Shutdown all PCs
    const shutdownPromises = pcInstances.map(async (pc) => {
      const pcIP = pc.ip_address || pc.tailscale_ip;
      if (!pcIP) return;

      // LAN first, Tailscale fallback.
      const client = createPCAgentClient([pc.ip_address, pc.tailscale_ip]);
      try {
        await client.shutdown();
        console.log(`Shutdown command sent to ${pc.pc_name || pcIP}`);
      } catch (error) {
        console.error(`Failed to shutdown ${pc.pc_name || pcIP}:`, error);
      }
    });

    await Promise.all(shutdownPromises);
    setShowDagAfsluiting(false);
    alert('Shutdown commands verzonden naar alle PCs');
  };

  // Dagstart, step 1: resolve what needs to happen and prove no one is mid-session.
  // The reactive `runningExperiencePcIds` only covers the mounted dashboard tiles,
  // so this re-asks every reachable agent directly — a reboot in the middle of a
  // visitor's run is exactly what this button must never do.
  const handleDagstartClick = async () => {
    if (!selectedRoomId) {
      alert('Selecteer eerst een ruimte.');
      return;
    }
    setDagstartChecking(true);
    try {
      // Two sets, deliberately different. Wake targets include the room NUCs,
      // which have no agent — a magic packet needs only a MAC. Restart targets
      // must not: there is nothing on :9090 to ask, and nothing to reboot safely.
      const wakeTargets = await databaseService.getWakeTargetsInRoom(selectedRoomId);
      const agentPcs = await databaseService.getPCInstancesInRoom(selectedRoomId);
      if (wakeTargets.length === 0) {
        alert("Geen pc's gevonden in deze ruimte.");
        return;
      }

      // A hard-off PC can't write status='offline', so heartbeat freshness — not
      // the status text alone — decides who gets restarted vs woken. Unmanaged
      // rows never heartbeat, so they always land in the wake set.
      const onlineIds = new Set(
        agentPcs
          .filter((pc) => pc.status === 'online' && databaseService.isPCOnline(pc.last_heartbeat))
          .map((pc) => pc.id)
      );
      const toRestart = agentPcs.filter((pc) => onlineIds.has(pc.id));
      const toWake = wakeTargets.filter((pc) => !onlineIds.has(pc.id));

      const busy = (
        await Promise.all(
          toRestart.map(async (pc) => {
            const client = createPCAgentClient([pc.ip_address, pc.tailscale_ip]);
            const status = await client.getStatus();
            return status?.current_experience ? pc.pc_name || pc.ip_address || pc.id : null;
          })
        )
      ).filter((name): name is string => !!name);

      if (busy.length > 0) {
        alert(`Dagstart geblokkeerd — er draait nog een experience op: ${busy.join(', ')}.`);
        return;
      }

      // PCs with a smart plug (infra.pc_power_control). Only the off ones matter:
      // a running gateway just restarts like every other PC.
      const gatewayIds = await databaseService.getGatewayPcIds(wakeTargets.map((pc) => pc.id));
      setDagstartPlan({
        toWake,
        toRestart,
        gateways: toWake.filter((pc) => gatewayIds.includes(pc.id)),
      });
    } catch (err: any) {
      alert(`Dagstart kon niet worden voorbereid: ${err?.message || err}`);
    } finally {
      setDagstartChecking(false);
    }
  };

  // Dagstart, step 2: execute the confirmed plan.
  const handleDagstartConfirm = async () => {
    if (!dagstartPlan) return;
    const { toWake, toRestart, gateways } = dagstartPlan;
    setDagstartBusy(true);
    try {
      // 1. Wake the off machines first, over both paths: a magic packet straight
      //    from this device (the only thing that works when the whole room is
      //    dark) plus the DB flag, so any peer that is about to be restarted can
      //    still pick it up and keep retrying. See services/wake.ts.
      if (toWake.length > 0) {
        setDagstartProgress(`${toWake.length} pc${toWake.length === 1 ? '' : "'s"} aanzetten via Wake-on-LAN…`);
        await wakeMachines(toWake);
      }

      // 2. Power-cycle the smart plug of any gateway PC that is off. With every
      //    online PC about to reboot there may be nobody left to send packets,
      //    so this out-of-band plug is what gets a fully dark room started.
      for (const gw of gateways) {
        setDagstartProgress(`Gateway ${gw.pc_name || ''} aanzetten via de slimme stekker…`);
        const res = await databaseService.powerOnGateway(gw.id);
        if (!res.success) {
          console.warn(`Gateway power-on failed for ${gw.pc_name}:`, res.error);
        }
      }

      // 3. Give the still-online peers one waker cycle before we take them down.
      //    Local packets have already gone out, so this only matters for targets
      //    this device cannot broadcast to — a second network in the room, or the
      //    web build, where peer agents are the only route.
      if (toWake.length > 0 && toRestart.length > 0) {
        setDagstartProgress('Wachten tot de wake-pakketten verstuurd zijn…');
        await new Promise((resolve) => setTimeout(resolve, DAGSTART_WAKE_GRACE_MS));
      }

      // 4. Restart everything that is already up.
      if (toRestart.length > 0) {
        setDagstartProgress(`${toRestart.length} pc${toRestart.length === 1 ? '' : "'s"} herstarten…`);
        await Promise.all(
          toRestart.map(async (pc) => {
            const client = createPCAgentClient([pc.ip_address, pc.tailscale_ip]);
            const res = await client.restartPC();
            if (!res.success) {
              console.error(`Restart failed for ${pc.pc_name || pc.ip_address}:`, res.error);
            }
          })
        );
      }

      setDagstartPlan(null);
      setRefreshTrigger((n) => n + 1);
    } finally {
      setDagstartBusy(false);
      setDagstartProgress(null);
    }
  };

  return (
    <SettingsProvider>
      <div className="app">
      <SupabaseEnvBadge />
      {/* Operator Login - Shows first */}
      {!operatorLoggedIn && (
        <OperatorLogin onSuccess={handleOperatorLogin} />
      )}

      {/* Checklist Screen - Shows after operator login */}
      {operatorLoggedIn && !checklistCompleted && (
        <Checklist onComplete={() => setChecklistCompleted(true)} />
      )}

      {/* Main App - Shows after checklist completion */}
      {checklistCompleted && (
        <>
          <header className="app-header">
            <div className="header-left">
              <h1>RealityOperator</h1>
              <UpdateChecker isAdmin={operatorIsAdmin} />
            </div>
            <div className="header-room-selector">
              {operatorIsAdmin && (
                <select
                  value={selectedRoomId || ''}
                  onChange={(e) => handleRoomChange(e.target.value)}
                >
                  <option value="">Select room...</option>
                  {rooms.map((room) => (
                    <option key={room.id} value={room.id}>
                      {room.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
            {currentOperator && (
              <div className="operator-info">
                {currentOperator.name && <strong>{currentOperator.name} · </strong>}
                {currentOperator.role}
                {currentOperator.department && ` - ${currentOperator.department}`}
                {operatorIsAdmin && <span style={{ marginLeft: '0.5rem', fontWeight: 'bold' }}>ADMIN</span>}
              </div>
            )}
            <div className="header-actions">
              <button
                className={`refresh-btn ${activeView === 'dashboard' ? '' : 'header-view-inactive'}`}
                onClick={() => setActiveView('dashboard')}
              >
                Dashboard
              </button>
              {operatorIsAdmin && (
                <button
                  className={`refresh-btn ${activeView === 'logs' ? '' : 'header-view-inactive'}`}
                  onClick={() => setActiveView('logs')}
                >
                  Logs
                </button>
              )}
              {operatorIsAdmin && (
                <button
                  className={`refresh-btn ${activeView === 'deployments' ? '' : 'header-view-inactive'}`}
                  onClick={() => setActiveView('deployments')}
                >
                  Deployments
                </button>
              )}
              {operatorIsAdmin && (
                <button
                  className={`refresh-btn ${activeView === 'admin' ? '' : 'header-view-inactive'}`}
                  onClick={() => setActiveView('admin')}
                >
                  Access
                </button>
              )}
              {operatorIsAdmin && (
                <button
                  className={`refresh-btn ${activeView === 'software' ? '' : 'header-view-inactive'}`}
                  onClick={() => setActiveView('software')}
                >
                  Software
                </button>
              )}
              {operatorIsAdmin && (
                <button
                  className={`refresh-btn ${activeView === 'settings' ? '' : 'header-view-inactive'}`}
                  onClick={() => setActiveView('settings')}
                >
                  Settings
                </button>
              )}
              {operatorIsAdmin && (
                <button
                  className={`refresh-btn ${activeView === 'testlab' ? '' : 'header-view-inactive'}`}
                  onClick={() => setActiveView('testlab')}
                >
                  Test Lab
                </button>
              )}
              {operatorIsAdmin && (
                <button
                  className={`refresh-btn ${activeView === 'insights' ? '' : 'header-view-inactive'}`}
                  onClick={() => setActiveView('insights')}
                >
                  Insights
                </button>
              )}
              {operatorIsAdmin && (
                <button
                  className={`refresh-btn ${activeView === 'filedrop' ? '' : 'header-view-inactive'}`}
                  onClick={() => setActiveView('filedrop')}
                >
                  File Drop
                </button>
              )}
              <button className="refresh-btn" onClick={() => setRefreshTrigger(n => n + 1)}>
                Refresh
              </button>
              {canCloseDag && (
                <button
                  className="dagstart-btn"
                  onClick={handleDagstartClick}
                  disabled={dagstartChecking || runningExperiencePcIds.length > 0}
                  title={
                    runningExperiencePcIds.length > 0
                      ? 'Er draait nog een experience in deze ruimte — Dagstart is geblokkeerd'
                      : "Zet alle uitstaande pc's aan en herstart de pc's die al aan staan"
                  }
                >
                  {dagstartChecking ? 'Bezig…' : 'Dagstart'}
                </button>
              )}
              {canCloseDag && (
                <button className="dagafsluiting-btn" onClick={() => setShowDagAfsluiting(true)}>
                  DagAfsluiting
                </button>
              )}
              <button className="operator-logout-btn" onClick={handleOperatorLogout}>
                Logout
              </button>
              {!kioskMode && (
                <button className="lock-kiosk-btn" onClick={handleLockKiosk}>
                  Lock Kiosk
                </button>
              )}
              {operatorIsAdmin && window.electron?.quitApp && (
                <button
                  className="quit-app-btn"
                  onClick={() => {
                    if (confirm('Are you sure you want to quit the application?')) {
                      window.electron?.quitApp();
                    }
                  }}
                >
                  Quit App
                </button>
              )}
            </div>
          </header>

          <main className="app-main">
            {activeView === 'dashboard' && (
              <Dashboard
                roomId={selectedRoomId || undefined}
                isAdmin={operatorIsAdmin}
                canViewBetaReleases={canViewBetaReleases}
                onPCInstancesChange={setPcInstances}
                onRunningExperiencesChange={setRunningExperiencePcIds}
                refreshTrigger={refreshTrigger}
              />
            )}
            {activeView === 'logs' && (
              <LogsView roomId={selectedRoomId || undefined} />
            )}
            {activeView === 'deployments' && operatorIsAdmin && (
              <DeploymentView isAdmin={operatorIsAdmin} />
            )}
            {activeView === 'admin' && operatorIsAdmin && currentOperator && (
              <AdminAccessView actor={currentOperator} />
            )}
            {activeView === 'software' && operatorIsAdmin && (
              <SoftwareView roomId={selectedRoomId || undefined} />
            )}
            {activeView === 'settings' && operatorIsAdmin && (
              <SettingsView
                roomId={selectedRoomId || undefined}
                roomName={rooms.find((r) => r.id === selectedRoomId)?.name}
              />
            )}
            {activeView === 'testlab' && operatorIsAdmin && (
              <TestLabView roomId={selectedRoomId || undefined} operator={currentOperator} />
            )}
            {activeView === 'insights' && operatorIsAdmin && (
              <InsightsView roomId={selectedRoomId || undefined} operator={currentOperator} />
            )}
            {activeView === 'filedrop' && operatorIsAdmin && (
              <FileDropView operator={currentOperator} />
            )}
          </main>
        </>
      )}

      <AdminUnlockDialog
        isVisible={showAdminDialog}
        onClose={() => setShowAdminDialog(false)}
      />

      {dagstartPlan && (
        <Dagstart
          plan={dagstartPlan}
          busy={dagstartBusy}
          progress={dagstartProgress}
          onConfirm={handleDagstartConfirm}
          onCancel={() => setDagstartPlan(null)}
        />
      )}

      {showDagAfsluiting && (
        <DagAfsluiting
          onComplete={handleDagAfsluitingComplete}
          onCancel={() => setShowDagAfsluiting(false)}
        />
      )}
      </div>
    </SettingsProvider>
  );
}
